import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {BsModalRef, BsModalService} from 'ngx-bootstrap';
import {
    DialCodeSelectorModalComponent
} from '../../../shared/components/profile-sections/dial-code-selector-modal/dial-code-selector-modal.component';
import {BsModalEventsEmitter} from '../../../shared/events/bsModal.events';
import {GenderViewModel} from '../../../shared/viewModels/gender.viewModel';
import {BodyTypeViewModel} from '../../../shared/viewModels/bodyType.viewModel';
import {UsersService} from '../../../shared/services/users.service';
import {NotificationsService} from '../../../shared/services/notification.service';
import {LocalisationService} from '../../../shared/services/localisation.service';
import {UserProfileViewModel, UserWeightHistory} from '../../../shared/viewModels/userProfile.viewModel';
import {MasterService} from '../../../shared/services/master.service';
import {InBodyDto} from '../../../shared/viewModels/master.viewModel';
import * as moment from 'moment';

declare const require: (path: string) => { [key: string]: string; };

@Component({
    selector: 'app-master-personal-info-section',
    templateUrl: './personal-info-section.component.html',
    styleUrls: ['./personal-info-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class MasterPersonalInfoSectionComponent implements OnInit {
    @Input() editable = true;
    @Input() notifications = true;
    @Input() useSwiper = true;
    @Input() id;

    areaChartData = [];
    areaChartLabels = [];
    areaChartOptions = {};
    areaChartColors = [
        {
            borderColor: 'rgba(255,99,132,1)',
            backgroundColor: 'rgba(255,99,132,.2)'
        }
    ];

    inBodyCharts = [];

    loading = true;
    firstName: FormControl = new FormControl('', [Validators.required]);
    surname: FormControl = new FormControl('', [Validators.required]);
    phone: FormControl = new FormControl('', [Validators.required]);
    age: FormControl = new FormControl('', [Validators.required, Validators.min(1), Validators.max(Infinity)]);
    genders: GenderViewModel[];
    currentlySelectedGender: GenderViewModel;
    mailCampaign = true;
    is_master_ready_profile = false;
    is_change_diary_disabled = false;
    hideAutoLoadRecipes = false;

    weight: FormControl = new FormControl('', [Validators.required]);
    target_weight: FormControl = new FormControl('', [Validators.required]);
    height: FormControl = new FormControl('', [Validators.required, Validators.min(1), Validators.max(Infinity)]);
    bodyTypes: BodyTypeViewModel[];
    currentlySelectedBodyType: BodyTypeViewModel;

    dialCodes: { [key: string]: string; };
    selectedPhoneDialCode = '';

    dialCodeModal: BsModalRef;

    constructor(
        private readonly usersService: UsersService,
        private readonly masterService: MasterService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        private readonly notificationService: NotificationsService,
        private readonly localisationService: LocalisationService,
        private readonly bsModalService: BsModalService,
        private readonly bsModalEventsEmitter: BsModalEventsEmitter,
    ) {
        this.dialCodes = require(`../../../../../../config/country_dial_codes.json`);
    }

    ngOnInit() {
        this.selectedPhoneDialCode = '+359';

        forkJoin(
            this.usersService.getUserGenders(),
            this.usersService.getUserBodyTypes(),
            this.masterService.getUserProfile(this.id),
            this.masterService.getWeightHistory(this.id),
            this.masterService.getInBodyHistory(this.id),
        )
            .subscribe(([genders, bodyTypes, userProfile, weightHistory, inBodyData]) => {
                this.genders = genders;
                this.bodyTypes = bodyTypes;
                this.loading = false;

                this.changeDetectorRef.detectChanges();

                this.initCurrentUserProfile(userProfile);
                this.initWeightHistory(weightHistory);
                this.initInBodyHistory(inBodyData);
            });
    }

    onGenderSelected(gender: string) {
        this.currentlySelectedGender = this.genders.find(g => g.gender === gender);
    }

    onSelectedBodyType(id: BodyTypeViewModel) {
        this.currentlySelectedBodyType = id;
    }

    updateProfile() {
        this.loading = true;
        this.masterService.updateUserProfile({
            id: this.id,
            first_name: this.firstName.value,
            last_name: this.surname.value,
            dial_code: this.selectedPhoneDialCode,
            phone: this.phone.value,
            age: this.age.value,
            mail_campaign: this.mailCampaign,
            // is_master_ready_profile: this.is_master_ready_profile,
            is_change_diary_disabled: this.is_change_diary_disabled,
            hide_auto_load_button: this.hideAutoLoadRecipes,
            weight: this.weight.value,
            target_weight: this.target_weight.value,
            body_type: {
                id: this.currentlySelectedBodyType.id
            },
            height: this.height.value,
            gender: {
                id: this.currentlySelectedGender.id
            }
        })
            .subscribe(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();

                if (this.notifications) {
                    this.notificationService.success(this.localisationService.localise('settings_profile_updated_successfully'));
                }
            });
    }

    getBodyTypeTranslationKey(bodyTypeId: number): string {
        if (bodyTypeId === 4) {
            return 'ectomorph_text';
        }

        if (bodyTypeId === 5) {
            return 'mesomorph_text';
        }

        return 'endomorph_text';
    }

    openDialCodeModal() {
        this.dialCodeModal = this.bsModalService.show(DialCodeSelectorModalComponent, {
            backdrop: true
        });

        this.dialCodeModal.content.personalOrWizardComponent = this;
        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.dialCodeModal);
    }

    get saveButtonIsDisabled(): boolean {
        return !this.weight.valid || !this.target_weight.valid || !this.phone.valid || !this.height.valid || !this.currentlySelectedBodyType || !this.firstName.valid || !this.surname.valid || !this.age.valid || !this.currentlySelectedGender;
    }

    private initCurrentUserProfile(userProfile: UserProfileViewModel) {
        this.firstName.setValue(userProfile.first_name);
        this.surname.setValue(userProfile.last_name);
        this.phone.setValue((userProfile.phone === null ? '' : userProfile.phone));
        this.age.setValue(userProfile.age);
        this.mailCampaign = userProfile.mail_campaign;
        // this.is_master_ready_profile = userProfile.is_master_ready_profile;
        this.is_change_diary_disabled = userProfile.is_change_diary_disabled;
        this.hideAutoLoadRecipes = userProfile.hide_auto_load_button;
        this.weight.setValue(userProfile.weight);
        this.target_weight.setValue(userProfile.target_weight);
        this.height.setValue(Math.floor(userProfile.height));

        if (userProfile.dial_code !== null && userProfile.dial_code.length > 0) {
            this.selectedPhoneDialCode = userProfile.dial_code;
        }

        this.currentlySelectedBodyType = userProfile.body_type ? this.bodyTypes.find(bt => bt.id === userProfile.body_type.id) : undefined;
        this.currentlySelectedGender = userProfile.gender ? this.genders.find(g => g.id === userProfile.gender.id) : undefined;

        this.changeDetectorRef.markForCheck();
    }

    onMailCampaignChange(mailCampaign: boolean) {
        this.mailCampaign = mailCampaign;
        this.changeDetectorRef.markForCheck();
    }

    onIsMasterReadyProfileChange(is_master_ready_profile: boolean) {
        this.is_master_ready_profile = is_master_ready_profile;
        this.changeDetectorRef.markForCheck();
    }

    onIsChangeDiaryDisabledChange(change: boolean) {
        this.is_change_diary_disabled = change;
        this.changeDetectorRef.markForCheck();
    }

    onHideAutoLoadChange(value: boolean) {
        this.hideAutoLoadRecipes = value;
        this.changeDetectorRef.markForCheck();
    }

    onDialCodeChange(event) {
        this.selectedPhoneDialCode = event.target.value;
        this.changeDetectorRef.markForCheck();
    }

    private initWeightHistory(weightHistory: UserWeightHistory[]) {
        const data = [];
        const labels = [];

        weightHistory.forEach(history => {
            data.push(history.weight);
            labels.push(history.created_at);
        });

        this.areaChartData.push({
            label: 'кг.',
            data: data,
            borderWidth: 2,
            fill: true
        });
        this.areaChartLabels = labels;

        this.changeDetectorRef.markForCheck();
    }

    private initInBodyHistory(inBodyData: InBodyDto) {
        const labels = [];
        const chartData = {};
        const area = {};

        const macros_mapping = {
            'TBW (Total Body Water)': 'Обем вода в тялото (литри)',
            'ICW (Intracellular Water)': 'Вътреклетъчна вода (литри)',
            'ECW (Extracellular Water)': 'Извънклетъчна вода (литри)',

            'SMM (Skeletal Muscle Mass)': 'Мазнини (кг)',
            'FFM of Right Arm': 'Мускулна маса (кг)',
            'FFM of Right Leg': 'Мазнини (%)',

            'BMR (Basal Metabolic Rate)': 'Метаболно ниво',
            'VFA (Visceral Fat Area)': 'Вътрешно омазняване',
            'BMC (Bone Mineral Content)': 'Минерално съдържание',
        };
        const colors = [
            {
                borderColor: 'rgba(75,182,228,1)',
                backgroundColor: 'rgba(75,182,228,.2)'
            },
            {
                borderColor: 'rgba(44,128,200,1)',
                backgroundColor: 'rgba(44,128,200,.2)'
            },
            {
                borderColor: 'rgba(13,44,118,1)',
                backgroundColor: 'rgba(13,44,118,.2)'
            },
            {
                borderColor: 'rgba(111,74,74,1)',
                backgroundColor: 'rgba(111,74,74,.2)'
            },
            {
                borderColor: 'rgba(41,143,112,1)',
                backgroundColor: 'rgba(41,143,112,.2)'
            },
            {
                borderColor: 'rgba(157,108,172,1)',
                backgroundColor: 'rgba(157,108,172,.2)'
            },
            {
                borderColor: 'rgba(161,196,14,1)',
                backgroundColor: 'rgba(161,196,14,.2)'
            },
            {
                borderColor: 'rgba(196,14,14,1)',
                backgroundColor: 'rgba(196,14,14,.2)'
            },
            {
                borderColor: 'rgba(196,14,151,1)',
                backgroundColor: 'rgba(196,14,151,.2)'
            }
        ];
        const usedColors = [];

        const returnKey = function (key) {
            for (const mappingKey of Object.keys(macros_mapping)) {
                if (key.includes(mappingKey)) {
                    return mappingKey;
                }
            }

            return null;
        };

        const returnColor = function () {
            for (const color of colors) {
                if (!usedColors.includes(color)) {
                    usedColors.push(color);
                    return color;
                }
            }

            return {
                borderColor: 'rgba(75,182,228,1)',
                backgroundColor: 'rgba(75,182,228,.2)'
            };
        };

        Object.keys(macros_mapping).forEach(key => {
            if (!area[key]) {
                area[key] = {
                    label: macros_mapping[key],
                    data: [],
                    borderWidth: 2,
                    fill: true
                };
            }

            if (!chartData[key]) {
                chartData[key] = [];
            }
        });

        inBodyData.data.forEach(data => {
            data.macros.forEach(macro => {
                const mappingKey = returnKey(macro.key);

                if (mappingKey) {
                    chartData[mappingKey].push(macro.value.replace(',', '.'));
                }
            });

            labels.push(moment(data.date + '').format('DD.MM.YYYY'));
        });

        Object.keys(chartData).forEach(key => {
            area[key].data = chartData[key];

            this.inBodyCharts.push({
                'title': area[key].label,
                'data': [area[key]],
                'labels': labels,
                'options': {},
                'colors': [returnColor()]
            });
        });

        console.log(this.inBodyCharts);

        this.changeDetectorRef.markForCheck();
    }
}
